import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import type { Service } from '@/types/service';

interface DeleteConfirmationProps {
  /** Service to delete/deactivate */
  service: Service | null;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
  /** Whether the delete operation is in progress */
  isDeleting?: boolean;
}

/**
 * Confirmation dialog for deactivating a service.
 * Shows service details and requires explicit confirmation.
 */
export function DeleteConfirmation({
  service,
  open,
  onOpenChange,
  onConfirm,
  isDeleting = false,
}: DeleteConfirmationProps) {
  const { t } = useTranslation('services');

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle>{t('delete.title')}</DialogTitle>
              <DialogDescription>{t('delete.warning')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground">
            {t('delete.confirm')}
          </p>
          <div className="mt-4 rounded-md border bg-muted/50 p-4">
            <p className="font-medium">{service.description}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{service.serviceId}</p>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            {t('delete.consequence')}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            {t('common:button.cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={isDeleting}>
            {t('common:button.deactivate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
